/**
 * @author    Nikko Renolds
 * @package   Trap Tycoon
 * @copyright (c) Fallox Developments 2026.
 * @license   MIT
 * @description Detailed management view for Runner units.
 */

import React, { useState } from "react"; // Added useState
import {
  Truck,
  Package,
  Activity,
  Zap,
  Filter,
  ArrowUpDown,
} from "lucide-react"; // Added Filter, ArrowUpDown

const RARITY_COLORS = {
  common: "text-zinc-400 border-zinc-700",
  rare: "text-blue-400 border-blue-700",
  epic: "text-purple-400 border-purple-700",
  legendary: "text-yellow-400 border-yellow-700",
};

export default function RunnersManagement({ inventory, onBack }) {
  const [rarityFilter, setRarityFilter] = useState("all");
  const [sortBy, setSortBy] = useState("cap_desc");
  const runners = inventory.filter((i) => i.type === "runner");
  const totalCapacity = runners.reduce(
    (acc, runner) =>
      acc + (runner.production_rate || 0) * runner.quantity * 100,
    0
  );

  const filteredRunners = runners
    .filter((r) => rarityFilter === "all" || r.rarity === rarityFilter)
    .sort((a, b) => {
      if (sortBy === "cap_desc") return b.production_rate - a.production_rate;
      if (sortBy === "cap_asc") return a.production_rate - b.production_rate;
      if (sortBy === "qty") return b.quantity - a.quantity;
      return 0;
    });

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-right duration-300">
      <div className="flex items-center justify-between border-b border-blue-900 pb-4">
        <div>
          <h2 className="text-3xl font-black italic text-blue-500 uppercase flex items-center gap-2">
            <Truck size={28} /> Logistics Network
          </h2>
          <p className="text-xs text-blue-400 font-mono uppercase tracking-widest">
            Street Distribution Control
          </p>
        </div>
        <button
          onClick={onBack}
          className="px-6 py-2 bg-zinc-900 border border-zinc-700 text-zinc-300 hover:text-white hover:border-white transition-all uppercase font-bold text-xs"
        >
          &larr; Back to Trap
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-black border border-blue-900/50 p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[10px] text-blue-700 uppercase font-bold flex items-center gap-1">
                <Activity size={12} /> Active Units
              </p>
              <p className="text-2xl font-black text-white">
                {runners.reduce((acc, r) => acc + r.quantity, 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-black border border-yellow-900/50 p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[10px] text-yellow-700 uppercase font-bold flex items-center gap-1">
                <Zap size={12} /> Total Move Capacity
              </p>
              <p className="text-2xl font-black text-yellow-400">
                ~{Math.round(totalCapacity)}% / Tick
              </p>
            </div>
            <div className="text-right">
              <p className="text-[9px] text-zinc-500 max-w-[150px]">
                Capacity combines across stacks but caps at 50% per tick per
                recipe.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 bg-zinc-950 p-3 border border-zinc-800 items-center">
        <div className="flex items-center gap-2 bg-black border border-zinc-700 px-3 py-1">
          <Filter size={14} className="text-zinc-500" />
          <select
            value={rarityFilter}
            onChange={(e) => setRarityFilter(e.target.value)}
            className="bg-transparent text-[10px] text-white outline-none uppercase cursor-pointer"
          >
            <option value="all" className="bg-black text-white">
              All Tiers
            </option>
            <option value="common" className="bg-black text-white">
              Common
            </option>
            <option value="rare" className="bg-black text-white">
              Rare
            </option>
            <option value="epic" className="bg-black text-white">
              Epic
            </option>
            <option value="legendary" className="bg-black text-white">
              Legendary
            </option>
          </select>
        </div>

        <div className="flex items-center gap-2 bg-black border border-zinc-700 px-3 py-1">
          <ArrowUpDown size={14} className="text-zinc-500" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-transparent text-[10px] text-white outline-none uppercase cursor-pointer"
          >
            <option value="cap_desc" className="bg-black text-white">
              Highest Capacity
            </option>
            <option value="cap_asc" className="bg-black text-white">
              Lowest Capacity
            </option>
            <option value="qty" className="bg-black text-white">
              Quantity
            </option>
          </select>
        </div>
      </div>

      {/* Unit List */}
      <div className="grid grid-cols-1 gap-3">
        {filteredRunners.length === 0 ? (
          <div className="p-8 text-center border-2 border-dashed border-zinc-800 text-zinc-600 font-mono uppercase text-xs">
            No runners found matching these filters.
          </div>
        ) : (
          filteredRunners.map((unit) => (
            <div
              key={unit.id}
              className={`bg-zinc-950 border-l-4 p-4 flex flex-col md:flex-row justify-between items-center gap-4 ${
                RARITY_COLORS[unit.rarity] || RARITY_COLORS.common
              } border-y border-r border-zinc-900`}
            >
              <div className="flex items-center gap-4 w-full md:w-auto">
                <div className="bg-black p-3 border border-zinc-800">
                  <Package
                    size={20}
                    className={RARITY_COLORS[unit.rarity]?.split(" ")[0]}
                  />
                </div>
                <div>
                  <h4 className="font-black text-white uppercase italic text-sm">
                    {unit.name}
                  </h4>
                  <p className="text-[10px] text-zinc-500 font-mono uppercase">
                    {unit.rarity} | Qty:{" "}
                    <span className="text-white">{unit.quantity}</span>
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-6 w-full md:w-auto justify-between md:justify-end">
                <div className="text-right">
                  <p className="text-[10px] text-zinc-500 uppercase font-bold">
                    Assignment
                  </p>
                  <p
                    className={`text-xs font-bold uppercase ${
                      unit.assigned_recipe_id
                        ? "text-blue-400"
                        : "text-zinc-600"
                    }`}
                  >
                    {unit.assigned_recipe_id ? "Moving Weight" : "Idle"}
                  </p>
                </div>

                <div className="text-right">
                  <p className="text-[10px] text-zinc-500 uppercase font-bold">
                    Capacity
                  </p>
                  <p className="text-sm font-bold text-yellow-400">
                    {Math.round(unit.production_rate * 100)}% / unit
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
